import { getVariationFillColor } from '@gonfalon/flag-variations/getVariationFillColor';
import type { List } from '@gonfalon/immutable-shim/immutableShim';
import { Map } from '@gonfalon/immutable-shim/immutableShim';
import clsx from 'clsx';

import { ClauseInstructionDescription } from '#components/InstructionList/ClauseInstructionDescription';
import { RolloutDescription } from '#components/InstructionList/RolloutDescription';
import Variation from '#components/Variation';
import type { Clause } from '#utils/clause/records/Clause';
import type { Variation as variationType } from '#utils/flags/records/Variation';
import type { AggregateRule } from '#utils/instructions/rules/types';

import { ExperimentRolloutDescription } from './ExperimentRolloutDescription';
import { RULE_NOT_FOUND } from './instructionListUtils';
import { RuleDescriptionKind } from './ruleDescriptionKind';

import styles from './styles.module.css';

export { RuleDescriptionKind } from './ruleDescriptionKind';

export const RuleInstructionDescription = ({
  projKey,
  envKey,
  rule,
  variations,
  showServeVariation = false,
  kind = RuleDescriptionKind.CLAUSE_SUMMARY,
}: {
  projKey: string;
  envKey: string;
  rule?: AggregateRule;
  showServeVariation?: boolean;
  variations: List<variationType>;
  kind?: RuleDescriptionKind;
}) => {
  if (!rule) {
    return <span className="u-italic">{RULE_NOT_FOUND}</span>;
  }

  const renderVariation = (index: number) => (
    <Variation
      key={index}
      color={getVariationFillColor(index)}
      value={
        /* eslint-disable @typescript-eslint/no-non-null-assertion */
        variations.get(index)!.getDisplay() /* eslint-enable @typescript-eslint/no-non-null-assertion */
      }
    />
  );

  const serveVariation = () => {
    let variationIndex = 0;
    if (rule.variationId) {
      variations.forEach((v, i) => {
        if (v._id === rule.variationId) {
          variationIndex = i;
        }
      });
    }
    const bucketBy = rule.rolloutBucketBy;
    const rolloutContextKind = rule.rolloutContextKind;
    const rolloutWeights = rule.rolloutWeights;
    const experimentAllocation = rule.experimentAllocation;

    let content;
    if (experimentAllocation && rolloutWeights) {
      content = (
        <ExperimentRolloutDescription
          rolloutWeights={Map(rolloutWeights)}
          variations={variations}
          experimentAllocation={experimentAllocation}
        />
      );
    } else if (rolloutWeights && !experimentAllocation) {
      content = (
        <RolloutDescription
          rolloutWeights={Map(rolloutWeights)}
          contextKind={rolloutContextKind}
          bucketBy={bucketBy}
          variations={variations}
        />
      );
    } else {
      content = renderVariation(variationIndex);
    }

    return (
      <span
        className={clsx('u-fw-regular', {
          [styles.servedVariation]: !(experimentAllocation && rolloutWeights),
        })}
      >
        serve {content}
      </span>
    );
  };

  const renderClause = () => (
    <>
      {rule.clause ? (
        <ClauseInstructionDescription projKey={projKey} envKey={envKey} kind={kind} clause={rule.clause} />
      ) : (
        rule.clauses.map((clause: Clause | null, indx: number) => {
          const clauseArrLength = rule.clauses.length;
          let extraText;
          if (indx !== clauseArrLength - 1) {
            extraText = ' and ';
          }
          return (
            <ClauseInstructionDescription
              projKey={projKey}
              envKey={envKey}
              kind={kind}
              key={indx}
              clause={
                /* eslint-disable @typescript-eslint/no-non-null-assertion */
                clause! /* eslint-enable @typescript-eslint/no-non-null-assertion */
              }
              extraText={extraText}
            />
          );
        })
      )}
    </>
  );

  if (kind === RuleDescriptionKind.DESCRIPTION_WITH_CLAUSE_SUMMARY && rule.description) {
    return (
      <>
        <span className="u-fw-semibold RuleInstructionDescription">{rule.description}</span>
        <div className="u-color-text-ui-tertiary u-ml-l RuleInstructionDescription--clauseSummary">
          {renderClause()}
          {showServeVariation && serveVariation()}
        </div>
      </>
    );
  }

  if (kind === RuleDescriptionKind.DESCRIPTION_ONLY && rule.description) {
    return <span className="u-fw-semibold RuleInstructionDescription">{rule.description}</span>;
  }

  return (
    <>
      {renderClause()}
      {showServeVariation && serveVariation()}
    </>
  );
};
